import { useMergedRefs } from '@sparemin/blockhead';
import cn from 'classnames';
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import EmbedPreview, { EmbedPreviewProps } from 'components/EmbedPreview';
import { EmbedPreviewInstance } from 'components/EmbedPreview/EmbedPreview';
import { VideoEditorPlaybackTimeProvider } from 'containers/VideoEditor';
import { createEmbedPreviewUrl } from 'redux/middleware/api/embed-service';
import { tokenSelector } from 'redux/modules/auth';
import bem from 'utils/bem';
import ClipSelectPlayer, { ClipSelectPlayerProps } from './ClipSelectPlayer';
import { ClipSelectPlayerInstance } from './ClipSelectPlayerContents';

const block = bem('clip-select-preview-player');

export type ClipSelectPreviewPlayerProps = Omit<
  ClipSelectPlayerProps,
  'children' | 'duration'
> &
  Pick<
    EmbedPreviewProps,
    'lastPlaybackPos' | 'aspectRatio' | 'onDestroy' | 'onReady'
  > & {
    widgetId: string | undefined;
  };

export type ClipSelectPreviewPlayerInstance = ClipSelectPlayerInstance & {
  getDuration: () => number;
};

const Preview = React.forwardRef<
  ClipSelectPreviewPlayerInstance,
  EmbedPreviewProps
>((props, ref) => {
  const previewRef = useRef<EmbedPreviewInstance>();

  useImperativeHandle(
    ref,
    () => ({
      /*
       * The embed preview cannot be played imperatively - instead it is controlled
       * by the `isPlaying` prop, which is updated any time play() or pause() is
       * called.  either approach can be used to control playback
       */
      pause: () => null,
      play: () => null,
      getCurrentTime: () => previewRef.current?.embedPlayer.getCurrentTime(),
      getDuration: () => previewRef.current?.embedPlayer.getDuration(),
      setCurrentTime: (sec: number) => {
        previewRef.current?.embedPlayer.setCurrentTime(sec);
      },
    }),
    [],
  );

  return (
    <VideoEditorPlaybackTimeProvider>
      <EmbedPreview ref={previewRef} {...props} />
    </VideoEditorPlaybackTimeProvider>
  );
});

const ClipSelectPreviewPlayer = React.forwardRef<
  ClipSelectPreviewPlayerInstance,
  ClipSelectPreviewPlayerProps
>(
  (
    {
      aspectRatio,
      className,
      onDestroy,
      onReady,
      isLoading,
      lastPlaybackPos,
      widgetId,
      ...props
    },
    forwardedRef,
  ) => {
    const previewRef = useRef<ClipSelectPreviewPlayerInstance>();
    const ref = useMergedRefs([forwardedRef, previewRef]);

    const authToken = useSelector(tokenSelector);
    const [duration, setDuration] = useState(0);

    const handleReady = useCallback(() => {
      setDuration(previewRef.current?.getDuration());
      onReady?.();
    }, [onReady]);

    const src = createEmbedPreviewUrl({ widgetId, token: authToken });

    return (
      <ClipSelectPlayer
        className={cn(block({ loading: isLoading }), className)}
        {...{ duration, isLoading, ref, src }}
        {...props}
      >
        {({
          className: playerClassName,
          isPlaying,
          onPause,
          onPlay,
          onTimeUpdate,
          ref: containerPlayerRef,
        }) => (
          <Preview
            {...{
              lastPlaybackPos,
              aspectRatio,
              onDestroy,
              onPause,
              onPlay,
              src,
            }}
            className={cn(block('preview'), playerClassName)}
            onTimeupdate={onTimeUpdate}
            onReady={handleReady}
            playing={isPlaying}
            ref={containerPlayerRef}
          />
        )}
      </ClipSelectPlayer>
    );
  },
);

export default ClipSelectPreviewPlayer;
